/**
 * ============================================================================
 * Iron Meridian - Diplomacy Modal Component
 * ============================================================================
 * 
 * Full-screen modal showing all diplomatic relationships with all countries.
 * Uses the new diplomacy engine with trust -100 to +100 and stance system.
 * Includes filtering, sorting, and trust meters for all nations.
 */

import { useState, useMemo } from 'react'
import { COUNTRY_BASELINES, getCountryBaseline } from '../../../shared/data/countryBaselines.js'
// DIPLOMATIC_STANCES uses string keys: 'ALLY', 'FRIENDLY', 'NEUTRAL', 'WARY', 'HOSTILE'
// TREATY_TYPES uses: 'ALLIANCE', 'NAP', 'TRADE', 'EMBARGO'

// ============================================================================
// CONSTANTS
// ============================================================================

const STANCE_CONFIG = {
    ALLY: { 
        label: 'Ally', 
        icon: '🛡️', 
        color: 'text-green-400', 
        bgColor: 'bg-green-500/20', 
        borderColor: 'border-green-500/50',
        barColor: 'bg-green-500'
    },
    FRIENDLY: { 
        label: 'Friendly', 
        icon: '😊', 
        color: 'text-blue-400', 
        bgColor: 'bg-blue-500/20', 
        borderColor: 'border-blue-500/50',
        barColor: 'bg-blue-500'
    },
    NEUTRAL: { 
        label: 'Neutral', 
        icon: '⚪', 
        color: 'text-gray-400', 
        bgColor: 'bg-gray-500/20', 
        borderColor: 'border-gray-500/30',
        barColor: 'bg-gray-500'
    },
    WARY: { 
        label: 'Wary', 
        icon: '😐', 
        color: 'text-orange-400', 
        bgColor: 'bg-orange-500/20', 
        borderColor: 'border-orange-500/50',
        barColor: 'bg-orange-500'
    },
    HOSTILE: { 
        label: 'Hostile', 
        icon: '⚔️', 
        color: 'text-red-400', 
        bgColor: 'bg-red-500/20', 
        borderColor: 'border-red-500/50',
        barColor: 'bg-red-500'
    }
}

const CONTROLLER_LABELS = {
    ai: { label: 'AI Power', color: 'text-amber-400', icon: '🤖' },
    neutral: { label: 'Minor Nation', color: 'text-gray-400', icon: '🏳️' }, // Fallback - should use getTierLabel()
    player: { label: 'Player', color: 'text-green-400', icon: '👤' }
}

/**
 * Get tier-based label for neutral (non-controlled) countries
 * @param {string} iso3 - Country ISO3 code
 * @returns {object} Label config with tier-based classification
 */
function getTierLabel(iso3) {
    const baseline = getCountryBaseline(iso3)
    const tierLabels = {
        superpower: { label: 'Superpower', color: 'text-yellow-400', icon: '⭐' },
        major: { label: 'Major Power', color: 'text-blue-400', icon: '🌟' },
        regional: { label: 'Regional Power', color: 'text-purple-400', icon: '✦' },
        minor: { label: 'Minor Nation', color: 'text-gray-400', icon: '🏳️' }
    }
    return tierLabels[baseline.tier] || tierLabels.minor
}

const PERSONALITY_LABELS = {
    aggressive: { label: 'Aggressive', color: 'text-red-400' },
    defensive: { label: 'Defensive', color: 'text-blue-400' },
    economic: { label: 'Economic', color: 'text-green-400' },
    opportunist: { label: 'Opportunist', color: 'text-yellow-400' },
    diplomatic: { label: 'Diplomatic', color: 'text-purple-400' }
}

const TREATY_CONFIG = {
    ALLIANCE: { label: 'Alliance', icon: '🛡️', color: 'text-green-400' },
    NAP: { label: 'Non-Aggression Pact', icon: '📜', color: 'text-blue-400' },
    TRADE: { label: 'Trade Agreement', icon: '💰', color: 'text-green-400' },
    EMBARGO: { label: 'Embargo', icon: '🚫', color: 'text-red-400' }
}

// Treaty type keys for proposing treaties (match diplomacyEngine)
const TREATY_TYPES = {
    ALLIANCE: 'ALLIANCE',
    NAP: 'NAP',
    TRADE: 'TRADE',
    EMBARGO: 'EMBARGO'
}

/**
 * Flag icon component using flagcdn.com (free, no API key needed)
 * Falls back to emoji globe if image fails to load
 */
function FlagIcon({ iso3, size = 'md', className = '' }) {
    // Complete ISO-3 to ISO-2 mapping (ISO 3166-1 standard + Natural Earth territories)
    // Covers all 186 countries/territories in the game
    const iso3ToIso2 = {
        'AFG': 'af', 'AGO': 'ao', 'AIA': 'ai', 'ALB': 'al', 'AND': 'ad', 
        'ARE': 'ae', 'ARG': 'ar', 'ARM': 'am', 'ATA': 'aq', 'ATF': 'tf',
        'AUS': 'au', 'AUT': 'at', 'AZE': 'az', 'BDI': 'bi', 'BEL': 'be', 
        'BEN': 'bj', 'BFA': 'bf', 'BGD': 'bd', 'BGR': 'bg', 'BHR': 'bh', 
        'BIH': 'ba', 'BLM': 'bl', 'BLR': 'by', 'BLZ': 'bz', 'BOL': 'bo', 
        'BRA': 'br', 'BRN': 'bn', 'BTN': 'bt', 'BVT': 'bv', 'BWA': 'bw',
        'CAF': 'cf', 'CAN': 'ca', 'CCK': 'cc', 'CHE': 'ch', 'CHL': 'cl', 
        'CHN': 'cn', 'CIV': 'ci', 'CMR': 'cm', 'COD': 'cd', 'COG': 'cg', 
        'COK': 'ck', 'COL': 'co', 'CRI': 'cr', 'CUB': 'cu', 'CXI': 'cx',
        'CYP': 'cy', 'CZE': 'cz', 'DEU': 'de', 'DJI': 'dj', 'DNK': 'dk', 
        'DOM': 'do', 'DZA': 'dz', 'ECU': 'ec', 'EGY': 'eg', 'ERI': 'er', 
        'ESH': 'eh', 'ESP': 'es', 'EST': 'ee', 'ETH': 'et', 'FIN': 'fi', 
        'FJI': 'fj', 'FLK': 'fk', 'FRA': 'fr', 'FSM': 'fm', 'GAB': 'ga', 
        'GBR': 'gb', 'GEO': 'ge', 'GHA': 'gh', 'GIN': 'gn', 'GLP': 'gp',
        'GMB': 'gm', 'GNB': 'gw', 'GNQ': 'gq', 'GRC': 'gr', 'GRL': 'gl', 
        'GTM': 'gt', 'GUF': 'gf', 'GUM': 'gu', 'GUY': 'gy', 'HKG': 'hk',
        'HMD': 'hm', 'HND': 'hn', 'HRV': 'hr', 'HTI': 'ht', 'HUN': 'hu', 
        'IDN': 'id', 'IND': 'in', 'IRL': 'ie', 'IRN': 'ir', 'IRQ': 'iq', 
        'ISL': 'is', 'ISR': 'il', 'ITA': 'it', 'JAM': 'jm', 'JOR': 'jo', 
        'JPN': 'jp', 'KAZ': 'kz', 'KEN': 'ke', 'KGZ': 'kg', 'KHM': 'kh', 
        'KNA': 'kn', 'KOR': 'kr', 'KWT': 'kw', 'LAO': 'la', 'LBN': 'lb', 
        'LBR': 'lr', 'LBY': 'ly', 'LCA': 'lc', 'LIE': 'li', 'LKA': 'lk', 
        'LSO': 'ls', 'LTU': 'lt', 'LUX': 'lu', 'LVA': 'lv', 'MAF': 'mf',
        'MAR': 'ma', 'MCO': 'mc', 'MDA': 'md', 'MDG': 'mg', 'MDV': 'mv', 
        'MEX': 'mx', 'MHL': 'mh', 'MKD': 'mk', 'MLI': 'ml', 'MMR': 'mm', 
        'MNE': 'me', 'MNG': 'mn', 'MOZ': 'mz', 'MRT': 'mr', 'MTQ': 'mq',
        'MUS': 'mu', 'MWI': 'mw', 'MYS': 'my', 'MYT': 'yt', 'NAM': 'na', 
        'NCL': 'nc', 'NER': 'ne', 'NGA': 'ng', 'NIC': 'ni', 'NLD': 'nl', 
        'NOR': 'no', 'NPL': 'np', 'NZL': 'nz', 'OMN': 'om', 'PAK': 'pk', 
        'PAN': 'pa', 'PCN': 'pn', 'PER': 'pe', 'PHL': 'ph', 'PLW': 'pw',
        'PNG': 'pg', 'POL': 'pl', 'PRI': 'pr', 'PRK': 'kp', 'PRT': 'pt', 
        'PRY': 'py', 'PYF': 'pf', 'QAT': 'qa', 'REU': 're', 'ROU': 'ro', 
        'RUS': 'ru', 'RWA': 'rw', 'SAU': 'sa', 'SDN': 'sd', 'SEN': 'sn', 
        'SGP': 'sg', 'SGS': 'gs', 'SHN': 'sh', 'SLB': 'sb', 'SLE': 'sl', 
        'SLV': 'sv', 'SMR': 'sm', 'SOM': 'so', 'SPM': 'pm', 'SRB': 'rs', 
        'SSD': 'ss', 'SUR': 'sr', 'SVK': 'sk', 'SVN': 'si', 'SWE': 'se', 
        'SWZ': 'sz', 'SYR': 'sy', 'TCD': 'td', 'TGO': 'tg', 'THA': 'th', 
        'TJK': 'tj', 'TKM': 'tm', 'TLS': 'tl', 'TTO': 'tt', 'TUN': 'tn', 
        'TUR': 'tr', 'TZA': 'tz', 'UGA': 'ug', 'UKR': 'ua', 'URY': 'uy', 
        'USA': 'us', 'UZB': 'uz', 'VAT': 'va', 'VCT': 'vc', 'VEN': 've', 
        'VNM': 'vn', 'VUT': 'vu', 'WLF': 'wf', 'WSM': 'ws', 'XKX': 'xk', 
        'YEM': 'ye', 'ZAF': 'za', 'ZMB': 'zm', 'ZWE': 'zw'
    }
    
    const sizeClasses = {
        sm: 'w-5 h-4',
        md: 'w-8 h-6',
        lg: 'w-10 h-8'
    }
    
    const iso2 = iso3ToIso2[iso3]
    
    if (!iso2) {
        // No mapping found - show blank/white flag fallback
        return (
            <div className={`${sizeClasses[size]} flex items-center justify-center bg-gray-100 border border-gray-300 rounded-sm`}>
                <span className="text-gray-400 text-xs">?</span>
            </div>
        )
    }
    
    return (
        <img
            src={`https://flagcdn.com/w40/${iso2}.png`}
            srcSet={`https://flagcdn.com/w80/${iso2}.png 2x`}
            alt={`${iso3} flag`}
            className={`${sizeClasses[size]} object-cover rounded-sm ${className}`}
            onError={(e) => {
                e.target.style.display = 'none'
                e.target.nextSibling?.classList.remove('hidden')
            }}
        />
    )
}

/**
 * Flag with fallback wrapper
 */
function FlagWithFallback({ iso3, size = 'md', className = '' }) {
    const sizeClasses = {
        sm: 'w-5 h-4',
        md: 'w-8 h-6', 
        lg: 'w-10 h-8'
    }
    
    return (
        <div className={`${sizeClasses[size]} flex items-center justify-center bg-war-room-700 rounded-sm overflow-hidden`}>
            <FlagIcon iso3={iso3} size={size} className={className} />
            <span className="hidden text-gray-500 text-xs">🌐</span>
        </div>
    )
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/**
 * Trust meter that displays -100 to +100 trust with color gradient
 * @param {number} trust - Trust value from -100 to +100
 * @param {boolean} compact - Whether to show compact version
 */
function TrustMeter({ trust, compact = false }) {
    const getColor = (value) => {
        if (value >= 60) return 'bg-green-500'      // ALLY threshold
        if (value >= 20) return 'bg-blue-500'       // FRIENDLY threshold
        if (value >= -19) return 'bg-gray-500'      // NEUTRAL range
        if (value >= -59) return 'bg-orange-500'    // WARY range
        return 'bg-red-500'                          // HOSTILE
    }

    const getSign = (value) => value > 0 ? '+' : ''

    if (compact) {
        return (
            <div className="flex items-center gap-2">
                <div className="w-20 h-2 bg-war-room-700 rounded-full overflow-hidden relative">
                    {/* Center line */}
                    <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-600 z-10" />
                    {/* Trust bar - grows from center */}
                    <div 
                        className={`absolute top-0 bottom-0 ${getColor(trust)} transition-all`}
                        style={{
                            left: trust >= 0 ? '50%' : `${50 + (trust / 2)}%`,
                            width: `${Math.abs(trust) / 2}%`
                        }}
                    />
                </div>
                <span className={`text-xs w-8 font-mono ${trust >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {getSign(trust)}{trust}
                </span>
            </div>
        )
    }

    return (
        <div className="w-full">
            <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-500">-100</span>
                <span className={`font-medium ${trust >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {getSign(trust)}{trust}
                </span>
                <span className="text-gray-500">+100</span>
            </div>
            <div className="h-3 bg-war-room-700 rounded-full overflow-hidden relative">
                {/* Center line */}
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-500 z-10" />
                {/* Trust bar */}
                <div 
                    className={`absolute top-0 bottom-0 ${getColor(trust)} transition-all duration-300`}
                    style={{
                        left: trust >= 0 ? '50%' : `${50 + (trust / 2)}%`,
                        width: `${Math.abs(trust) / 2}%`
                    }}
                />
                {/* Threshold markers */}
                <div className="absolute top-0 bottom-0 left-[20%] w-px bg-gray-600/50" />
                <div className="absolute top-0 bottom-0 left-[40%] w-px bg-gray-600/50" />
                <div className="absolute top-0 bottom-0 left-[60%] w-px bg-gray-600/50" />
                <div className="absolute top-0 bottom-0 left-[80%] w-px bg-gray-600/50" />
            </div>
        </div>
    )
}

/**
 * Country row in the list
 */
function CountryRow({ entry, currentTurn, onSelect, isOwned }) {
    const stanceConfig = STANCE_CONFIG[entry.stance] || STANCE_CONFIG.NEUTRAL
    
    // Use dynamic tier-based labels for neutral countries, otherwise use controller type
    const controller = entry.controller === 'neutral' 
        ? getTierLabel(entry.iso3 || entry.countryId)
        : (CONTROLLER_LABELS[entry.controller] || CONTROLLER_LABELS.neutral)
    
    const personality = entry.personality ? PERSONALITY_LABELS[entry.personality] : null

    // Use iso3 as countryId (they're the same thing)
    const countryId = entry.iso3 || entry.countryId
    const countryName = entry.countryName || entry.name || countryId
    const activeTreaties = entry.treaties?.filter(t => !t.expiresTurn || t.expiresTurn > currentTurn) || []

    // Owned territory styling
    if (isOwned) {
        return (
            <button
                onClick={() => onSelect(entry)}
                className="w-full p-3 flex items-center gap-3 rounded-lg border border-green-500/50 bg-green-500/10 hover:bg-green-500/20 transition-colors text-left"
            >
                {/* Country flag */}
                <div className="w-10 h-8 rounded bg-war-room-700 flex items-center justify-center shrink-0 overflow-hidden">
                    <FlagWithFallback iso3={countryId} size="md" />
                </div>

                {/* Country info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="font-medium text-white truncate">{countryName}</span>
                        <span className="text-green-400">🏴</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                        <span className="text-green-400">Conquered Territory</span>
                        <span className="text-gray-600">•</span>
                        <span className="text-gray-500">{countryId}</span>
                    </div>
                </div>

                {/* Owned badge */}
                <div className="shrink-0">
                    <span className="px-2 py-1 rounded bg-green-500/20 text-green-400 text-xs font-medium">
                        Your Empire
                    </span>
                </div>
            </button>
        )
    }

    return (
        <button
            onClick={() => onSelect(entry)}
            className={`w-full p-3 flex items-center gap-3 rounded-lg border ${stanceConfig.borderColor} ${stanceConfig.bgColor} hover:bg-white/5 transition-colors text-left`}
        >
            {/* Country flag */}
            <div className="w-10 h-8 rounded bg-war-room-700 flex items-center justify-center shrink-0 overflow-hidden">
                <FlagWithFallback iso3={countryId} size="md" />
            </div>

            {/* Country info */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className="font-medium text-white truncate">{countryName}</span>
                    <span className={`text-xs ${stanceConfig.color}`}>{stanceConfig.icon}</span>
                    {activeTreaties.length > 0 && (
                        <span className="text-xs text-blue-400">
                            {activeTreaties.map(t => TREATY_CONFIG[t.type]?.icon || '📄').join('')}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2 text-xs">
                    <span className={controller.color}>{controller.label}</span>
                    {personality && (
                        <>
                            <span className="text-gray-600">•</span>
                            <span className={personality.color}>{personality.label}</span>
                        </>
                    )}
                    <span className="text-gray-600">•</span>
                    <span className="text-gray-500">{countryId}</span>
                </div>
            </div>

            {/* Trust */}
            <div className="shrink-0 w-28">
                <TrustMeter trust={entry.trust} compact />
            </div>

            {/* Stance badge */}
            <div className={`shrink-0 px-2 py-1 rounded text-xs ${stanceConfig.bgColor} ${stanceConfig.color} border ${stanceConfig.borderColor}`}>
                {stanceConfig.label}
            </div>
        </button>
    )
}

/**
 * Country detail panel
 */
function CountryDetail({ entry, currentTurn, onClose, onProposeTreaty, onCancelTreaty, isOwned }) {
    const stanceConfig = STANCE_CONFIG[entry.stance] || STANCE_CONFIG.NEUTRAL
    const controller = CONTROLLER_LABELS[entry.controller] || CONTROLLER_LABELS.neutral
    const personality = entry.personality ? PERSONALITY_LABELS[entry.personality] : null

    // Use iso3 as countryId (they're the same thing)
    const countryId = entry.iso3 || entry.countryId
    const countryName = entry.countryName || entry.name || countryId
    const activeTreaties = entry.treaties?.filter(t => !t.expiresTurn || t.expiresTurn > currentTurn) || []

    // Owned territory detail view
    if (isOwned) {
        return (
            <div className="bg-war-room-800 rounded-xl border border-green-500/30 p-4 space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-10 rounded-lg bg-war-room-700 flex items-center justify-center overflow-hidden">
                            <FlagWithFallback iso3={countryId} size="lg" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-white">{countryName}</h3>
                            <div className="flex items-center gap-2 text-sm">
                                <span className="text-gray-500 font-mono">{countryId}</span>
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-white transition"
                    >
                        ✕
                    </button>
                </div>

                {/* Owned Status */}
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                    <div className="flex items-center gap-3 mb-3">
                        <span className="text-2xl">🏴</span>
                        <div>
                            <span className="font-bold text-green-400 text-lg">Conquered Territory</span>
                            <p className="text-sm text-gray-400">This nation is now part of your empire</p>
                        </div>
                    </div>
                </div>

                {/* Stats if available */}
                {(entry.military || entry.economy) && (
                    <div>
                        <h4 className="text-sm font-medium text-gray-300 mb-2">Territory Resources</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="p-2 rounded bg-war-room-700/50">
                                <span className="text-gray-400">Military</span>
                                <span className="float-right text-red-400">⚔️ {entry.military || 0}</span>
                            </div>
                            <div className="p-2 rounded bg-war-room-700/50">
                                <span className="text-gray-400">Economy</span>
                                <span className="float-right text-yellow-400">💰 {entry.economy || 0}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Info note */}
                <div className="p-3 rounded bg-war-room-700/30 border border-war-room-600/30">
                    <p className="text-xs text-gray-500">
                        💡 Conquered territories contribute to your empire's total resources. 
                        Diplomatic actions are not available for your own territories.
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className="bg-war-room-800 rounded-xl border border-war-room-600/50 p-4 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-10 rounded-lg bg-war-room-700 flex items-center justify-center overflow-hidden">
                        <FlagWithFallback iso3={countryId} size="lg" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-white">{countryName}</h3>
                        <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-500 font-mono">{countryId}</span>
                            <span className="text-gray-600">•</span>
                            <span className={controller.color}>{controller.label}</span>
                            {personality && (
                                <>
                                    <span className="text-gray-600">•</span>
                                    <span className={personality.color}>{personality.label}</span>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="p-2 text-gray-400 hover:text-white transition"
                >
                    ✕
                </button>
            </div>

            {/* Stance */}
            <div className={`p-3 rounded-lg ${stanceConfig.bgColor} border ${stanceConfig.borderColor}`}>
                <div className="flex items-center gap-2">
                    <span className="text-xl">{stanceConfig.icon}</span>
                    <span className={`font-medium ${stanceConfig.color}`}>{stanceConfig.label}</span>
                    {entry.previousStance && entry.previousStance !== entry.stance && (
                        <span className="text-xs text-gray-500">
                            (was {STANCE_CONFIG[entry.previousStance]?.label || entry.previousStance})
                        </span>
                    )}
                </div>
            </div>

            {/* Trust */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">Trust Level</h4>
                <TrustMeter trust={entry.trust} />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Hostile</span>
                    <span>Wary</span>
                    <span>Neutral</span>
                    <span>Friendly</span>
                    <span>Ally</span>
                </div>
            </div>

            {/* Trust History */}
            {entry.trustHistory?.length > 0 && (
                <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Recent Changes</h4>
                    <div className="space-y-1 max-h-24 overflow-y-auto">
                        {entry.trustHistory.slice(-5).reverse().map((change, i) => (
                            <div key={i} className="text-xs flex items-center gap-2">
                                <span className="text-gray-500">Turn {change.turn}:</span>
                                <span className={change.delta >= 0 ? 'text-green-400' : 'text-red-400'}>
                                    {change.delta >= 0 ? '+' : ''}{change.delta}
                                </span>
                                <span className="text-gray-400">{change.reason}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Active Treaties */}
            {activeTreaties.length > 0 && (
                <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Active Treaties</h4>
                    <div className="space-y-2">
                        {activeTreaties.map((treaty, i) => {
                            const config = TREATY_CONFIG[treaty.type] || { label: treaty.type, icon: '📄', color: 'text-gray-400' }
                            return (
                                <div key={i} className="flex items-center justify-between p-2 rounded bg-war-room-700/50">
                                    <span className={`text-sm ${config.color}`}>
                                        {config.icon} {config.label}
                                    </span>
                                    {treaty.expiresTurn && (
                                        <span className="text-xs text-gray-500">
                                            Expires turn {treaty.expiresTurn}
                                        </span>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}

            {/* Treaty Options - only for AI countries */}
            {entry.controller === 'ai' && (
                <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Diplomatic Actions</h4>
                    <div className="space-y-2">
                        {/* Show propose options if friendly enough */}
                        {entry.trust >= 0 && !activeTreaties.some(t => t.type === TREATY_TYPES.NAP) && (
                            <button
                                onClick={() => onProposeTreaty?.(countryId, TREATY_TYPES.NAP, 10)}
                                className="w-full p-2 rounded bg-blue-600/30 hover:bg-blue-600/50 text-blue-300 text-sm transition"
                            >
                                📜 Propose Non-Aggression Pact
                            </button>
                        )}
                        {entry.trust >= 40 && !activeTreaties.some(t => t.type === TREATY_TYPES.TRADE) && (
                            <button
                                onClick={() => onProposeTreaty?.(countryId, TREATY_TYPES.TRADE, null)}
                                className="w-full p-2 rounded bg-green-600/30 hover:bg-green-600/50 text-green-300 text-sm transition"
                            >
                                💰 Propose Trade Agreement
                            </button>
                        )}
                        {entry.trust >= 60 && !activeTreaties.some(t => t.type === TREATY_TYPES.ALLIANCE) && (
                            <button
                                onClick={() => onProposeTreaty?.(countryId, TREATY_TYPES.ALLIANCE, null)}
                                className="w-full p-2 rounded bg-green-600/30 hover:bg-green-600/50 text-green-300 text-sm transition"
                            >
                                🛡️ Propose Alliance
                            </button>
                        )}
                        {/* Cancel treaty option */}
                        {activeTreaties.length > 0 && (
                            <button
                                onClick={() => onCancelTreaty?.(countryId, activeTreaties[0].type)}
                                className="w-full p-2 rounded bg-red-600/30 hover:bg-red-600/50 text-red-300 text-sm transition"
                            >
                                ⚠️ Break Treaty (Trust penalty)
                            </button>
                        )}
                        {/* Hostile options */}
                        {entry.trust < -20 && entry.stance !== 'HOSTILE' && (
                            <div className="text-xs text-gray-500 italic">
                                Relations are deteriorating...
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function DiplomacyModal({ 
    isOpen,
    onClose,
    diplomacyState = {},
    countries = {},
    playerCountry = null,
    pendingProposals = [],
    onProposeTreaty,
    onAcceptProposal,
    onRejectProposal,
    onCancelTreaty
}) {
    const [searchQuery, setSearchQuery] = useState('')
    const [filterType, setFilterType] = useState('all') // all, ai, neutral, allies, hostile, owned
    const [sortBy, setSortBy] = useState('name') // name, trust, stance
    const [selectedEntry, setSelectedEntry] = useState(null)

    // Get current turn from diplomacy state (default to 1)
    const currentTurn = diplomacyState?.currentTurn || 1

    // Helper to check if a country is owned by player (using live countries data)
    const isCountryOwned = (iso3) => {
        if (!iso3 || !countries) return false
        const country = countries[iso3]
        // A country is owned if it's player-controlled but NOT the home country
        return country?.controller === 'player' && iso3 !== playerCountry
    }

    // Get relationships as array
    const relationships = useMemo(() => {
        if (!diplomacyState?.relationships) return []
        return Object.values(diplomacyState.relationships)
    }, [diplomacyState])

    // Filter and sort relationships
    const filteredRelationships = useMemo(() => {
        let result = [...relationships]

        // Filter by search query
        if (searchQuery) {
            const query = searchQuery.toLowerCase()
            result = result.filter(r => 
                (r.countryName || r.name || '')?.toLowerCase().includes(query) ||
                (r.iso3 || r.countryId || '')?.toLowerCase().includes(query)
            )
        }

        // Filter by type
        if (filterType === 'ai') {
            result = result.filter(r => r.controller === 'ai')
        } else if (filterType === 'neutral') {
            result = result.filter(r => r.controller === 'neutral' && !isCountryOwned(r.iso3 || r.countryId))
        } else if (filterType === 'allies') {
            result = result.filter(r => (r.stance === 'ALLY' || r.stance === 'FRIENDLY') && !isCountryOwned(r.iso3 || r.countryId))
        } else if (filterType === 'hostile') {
            result = result.filter(r => (r.stance === 'HOSTILE' || r.stance === 'WARY') && !isCountryOwned(r.iso3 || r.countryId))
        } else if (filterType === 'treaty') {
            result = result.filter(r => r.treaties?.length > 0 && !isCountryOwned(r.iso3 || r.countryId))
        } else if (filterType === 'owned') {
            result = result.filter(r => isCountryOwned(r.iso3 || r.countryId))
        }

        // Sort
        if (sortBy === 'name') {
            result.sort((a, b) => (a.countryName || a.name || a.iso3 || '').localeCompare(b.countryName || b.name || b.iso3 || ''))
        } else if (sortBy === 'trust') {
            result.sort((a, b) => b.trust - a.trust)
        } else if (sortBy === 'stance') {
            const stanceOrder = {
                ALLY: 0,
                FRIENDLY: 1,
                NEUTRAL: 2,
                WARY: 3,
                HOSTILE: 4
            }
            result.sort((a, b) => (stanceOrder[a.stance] ?? 2) - (stanceOrder[b.stance] ?? 2))
        }

        return result
    }, [relationships, searchQuery, filterType, sortBy, countries, playerCountry])

    // Stats
    const stats = useMemo(() => {
        const ownedCount = relationships.filter(r => isCountryOwned(r.iso3 || r.countryId)).length
        const aiCount = relationships.filter(r => r.controller === 'ai' && !isCountryOwned(r.iso3 || r.countryId)).length
        const allyCount = relationships.filter(r => r.stance === 'ALLY' && !isCountryOwned(r.iso3 || r.countryId)).length
        const friendlyCount = relationships.filter(r => r.stance === 'FRIENDLY' && !isCountryOwned(r.iso3 || r.countryId)).length
        const hostileCount = relationships.filter(r => r.stance === 'HOSTILE' && !isCountryOwned(r.iso3 || r.countryId)).length
        const avgTrust = relationships.length > 0 
            ? Math.round(relationships.reduce((sum, r) => sum + r.trust, 0) / relationships.length)
            : 0
        return { ownedCount, aiCount, allyCount, friendlyCount, hostileCount, avgTrust, total: relationships.length }
    }, [relationships, countries, playerCountry])

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
            <div className="w-full max-w-4xl max-h-[90vh] m-4 bg-war-room-900 rounded-xl shadow-2xl overflow-hidden flex flex-col border border-war-room-600/50">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 bg-war-room-800 border-b border-war-room-600/50">
                    <div>
                        <h2 className="text-xl font-bold text-white flex items-center gap-2">
                            🌐 World Diplomacy
                        </h2>
                        <p className="text-sm text-gray-400">
                            {stats.total} nations • Avg trust: <span className={stats.avgTrust >= 0 ? 'text-green-400' : 'text-red-400'}>{stats.avgTrust >= 0 ? '+' : ''}{stats.avgTrust}</span>
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-white transition"
                    >
                        ✕
                    </button>
                </div>

                {/* Stats bar */}
                <div className="px-6 py-3 bg-war-room-800/50 border-b border-war-room-600/30 flex gap-4 text-sm flex-wrap">
                    {stats.allyCount > 0 && (
                        <span className="text-green-400">
                            🛡️ {stats.allyCount} {stats.allyCount === 1 ? 'Ally' : 'Allies'}
                        </span>
                    )}
                    {stats.friendlyCount > 0 && (
                        <span className="text-blue-400">
                            😊 {stats.friendlyCount} Friendly
                        </span>
                    )}
                    {stats.hostileCount > 0 && (
                        <span className="text-red-400">
                            ⚔️ {stats.hostileCount} Hostile
                        </span>
                    )}
                    {pendingProposals.length > 0 && (
                        <span className="text-purple-400 animate-pulse">
                            📨 {pendingProposals.length} Pending Proposals
                        </span>
                    )}
                </div>

                {/* Filters */}
                <div className="px-6 py-3 bg-war-room-800/30 border-b border-war-room-600/30 flex flex-wrap gap-3">
                    {/* Search */}
                    <div className="flex-1 min-w-[200px]">
                        <input
                            type="text"
                            placeholder="Search countries..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full px-3 py-1.5 rounded-lg bg-war-room-700 border border-war-room-600 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-amber-500/50"
                        />
                    </div>

                    {/* Filter buttons */}
                    <div className="flex gap-1 flex-wrap">
                        {[
                            { id: 'all', label: 'All' },
                            { id: 'owned', label: `Your Empire${stats.ownedCount > 0 ? ` (${stats.ownedCount})` : ''}` },
                            { id: 'ai', label: 'Major Powers' },
                            { id: 'allies', label: 'Allies/Friendly' },
                            { id: 'hostile', label: 'Hostile/Wary' },
                            { id: 'treaty', label: 'With Treaty' }
                        ].map(f => (
                            <button
                                key={f.id}
                                onClick={() => setFilterType(f.id)}
                                className={`px-3 py-1.5 rounded-lg text-xs transition ${
                                    filterType === f.id
                                        ? f.id === 'owned' 
                                            ? 'bg-green-600/50 text-green-200 border border-green-500/50'
                                            : 'bg-amber-600/50 text-amber-200 border border-amber-500/50'
                                        : 'bg-war-room-700 text-gray-400 border border-war-room-600 hover:bg-war-room-600'
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>

                    {/* Sort */}
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="px-3 py-1.5 rounded-lg bg-war-room-700 border border-war-room-600 text-gray-300 text-sm focus:outline-none"
                    >
                        <option value="name">Sort by Name</option>
                        <option value="trust">Sort by Trust</option>
                        <option value="stance">Sort by Stance</option>
                    </select>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden flex">
                    {/* List */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-2">
                        {filteredRelationships.length === 0 ? (
                            <div className="text-center text-gray-500 py-8">
                                {relationships.length === 0 
                                    ? 'No diplomatic relationships established yet'
                                    : 'No countries match your filters'
                                }
                            </div>
                        ) : (
                            filteredRelationships.map(entry => (
                                <CountryRow
                                    key={entry.iso3 || entry.countryId}
                                    entry={entry}
                                    currentTurn={currentTurn}
                                    onSelect={setSelectedEntry}
                                    isOwned={isCountryOwned(entry.iso3 || entry.countryId)}
                                />
                            ))
                        )}
                    </div>

                    {/* Detail panel */}
                    {selectedEntry && (
                        <div className="w-80 border-l border-war-room-600/50 p-4 overflow-y-auto">
                            <CountryDetail
                                entry={selectedEntry}
                                currentTurn={currentTurn}
                                onClose={() => setSelectedEntry(null)}
                                onProposeTreaty={onProposeTreaty}
                                onCancelTreaty={onCancelTreaty}
                                isOwned={isCountryOwned(selectedEntry.iso3 || selectedEntry.countryId)}
                            />
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-3 bg-war-room-800 border-t border-war-room-600/50 text-xs text-gray-500 flex items-center justify-between">
                    <span>💡 Click on a country to view details and manage diplomatic relations</span>
                    <span className="text-gray-600">Press <kbd className="px-1 py-0.5 rounded bg-war-room-700 text-gray-400">D</kbd> to close</span>
                </div>
            </div>
        </div>
    )
}
